import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider, onlineManager } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import type { UserRow } from '@solungo/core/schema';
import { routeTree } from './routeTree.gen';
import { meQuery, queryClient } from './state/queries';
import { initErrorTracking } from './lib/errors';
import { forgetUser, identifyUser, initAnalytics, trackScreen } from './lib/analytics';
import { onUnauthorized } from './api/client';
import { wipeDatabase } from './db/db';
import { storage } from './lib/storage';
import { isOverlay, seedOverlayHistory } from './lib/history';
import './app.css';

initErrorTracking();
initAnalytics();

// TanStack Query assumes a network until it sees an `offline` event, which a cold start in
// airplane mode never fires; without this, coming back online would not resume its refetches.
onlineManager.setOnline(navigator.onLine);

// A document opened straight on an overlay (a reload on a card, iOS restoring a suspended PWA)
// gets the list written into its history first, so back and the swipe gesture stay in-document.
seedOverlayHistory(window, storage.getLastDictionary());

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // The router scrolls the window to the top on every navigation: opening an overlay would first
  // jump the list under it to its header. Overlays keep the page as it is, and closing one
  // restores the list's own position
  scrollRestoration: ({ location }) => !isOverlay(location.pathname),
});

// Every /me the app learns (the stored user, the session probe, a sign-in, a profile edit) names
// the analytics profile (src/lib/analytics.ts)
queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== 'updated' || event.query.queryKey[0] !== meQuery.queryKey[0]) return;
  const user = (event.query.state.data as { user?: UserRow } | undefined)?.user;
  if (user) identifyUser(user);
});

// One screen view per resolved navigation, with the matched route's shape (src/lib/analytics.ts)
router.subscribe('onResolved', ({ toLocation }) => {
  trackScreen(toLocation.pathname, router.state.matches.at(-1)?.fullPath ?? '/');
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// A 401 anywhere means the session ended (revoked on another device, expired): wipe local data
// and go to sign-in (docs/project.md §8: signing out wipes the database).
onUnauthorized(() => {
  forgetUser();
  queryClient.clear();
  void wipeDatabase().finally(() => {
    storage.clearAll();
    void router.navigate({ to: '/auth', replace: true });
  });
});

// A route's error boundary catches render and loader errors before window.onerror could see them;
// React hands every one of them over here (docs/project.md §12).
const reactErrors = Sentry.reactErrorHandler((error, errorInfo) => {
  if (import.meta.env.DEV) console.error(error, errorInfo.componentStack);
});

createRoot(document.getElementById('root')!, {
  onUncaughtError: reactErrors,
  onCaughtError: reactErrors,
  onRecoverableError: reactErrors,
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
